/**
 * ВЫБОР ПОЛУЧАТЕЛЯ ДЛЯ ПЕРЕСЫЛКИ СООБЩЕНИЯ (Антон 05.10 12:37).
 *
 * ЭТАЛОН — iOS `ForwardMessageView.swift` (коммит 0591c6d). Повторено:
 *  - список комнат — ТОТ ЖЕ источник, что у списка чатов
 *    (`useRoomLists` → buildRoomLists), а не свой запрос: два списка
 *    комнат разошлись бы порядком, именами и составом на первом же sync'е;
 *  - пространства исключены (у них нет ленты сообщений — пересланный текст
 *    провалился бы в никуда): в RoomLists они уже отдельным массивом;
 *  - ТЕКУЩАЯ комната НЕ исключается: «переслать самому себе в тот же чат» —
 *    законный приём, так поднимают старое сообщение в конец ленты;
 *  - экран НЕ закрывается после первой отправки: переслать одно сообщение
 *    в три чата подряд — обычное дело; уже отправленные строки получают
 *    галочку, иначе человек слал бы дубли;
 *  - пока идёт отправка — спиннер в ТОЙ строке, по которой нажали, и весь
 *    список заблокирован от повторного нажатия (двойной тап дал бы два
 *    одинаковых сообщения у получателя);
 *  - отказ показывается ЗДЕСЬ, в диалоге (в вебе нет общего баннера
 *    sendError, который iOS рисует в RoomView под листом).
 *
 * РАСХОЖДЕНИЕ С iOS ОДНО, платформенное: выбор получателя — по одной
 * комнате за нажатие (как в iOS), но на десктопе диалог закрывается крестиком
 * / Esc / кликом по затемнению — стандарт Dialog.tsx, а не кнопкой
 * «Закрыть» в тулбаре, которой на веб-платформе нет.
 */
import { useMemo, useState } from "react";
import type { MatrixClient, MatrixEvent } from "matrix-js-sdk";

import { useT, useTF } from "../i18n";
import { humanError } from "../matrix/roomActions";
import { forwardMessage } from "../matrix/forward";
import { useAuthedAvatar } from "../matrix/media";
import type { TimelineItem } from "../matrix/timeline";
import type { RoomSummary } from "../matrix/rooms";
import { useRoomLists } from "../matrix/useMatrixRooms";
import { Dialog, DialogButton } from "./Dialog";
import { palette, s } from "./styles";
import { color, colorOf, font, radius, size, space } from "./theme";

export function ForwardDialog({
  client,
  item,
  event,
  onClose,
}: {
  client: MatrixClient;
  /** Что пересылаем. Готовый TimelineItem, а не текст: решение «можно ли
      это вообще переслать» принимает сервис (canForwardMessage), и
      принимать его второй раз здесь значило бы два расходящихся правила. */
  item: TimelineItem;
  /** Исходное событие: пересылается ЕГО content (с url/file/info,
      голосовыми полями), а не проекция TimelineItem для рендера. */
  event: MatrixEvent | null;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const lists = useRoomLists(client);
  const [query, setQuery] = useState("");
  /** Куда уже переслали за это открытие диалога — по id комнаты. */
  const [sentTo, setSentTo] = useState<ReadonlySet<string>>(new Set());
  /** Комната, по которой прямо сейчас идёт отправка. null — ни одной. */
  const [sendingTo, setSendingTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    // Пространств в `joined` нет по построению buildRoomLists — фильтровать
    // isSpace второй раз не нужно (и нельзя: второго правила быть не должно).
    if (!q) return lists.joined;
    // toLocaleLowerCase, А НЕ toLowerCase: второй врёт на турецком «i»
    // (тот же довод, что у iOS-поиска localizedCaseInsensitiveContains).
    return lists.joined.filter((r) => r.name.toLocaleLowerCase().includes(q));
  }, [lists.joined, query]);

  const forward = async (room: RoomSummary) => {
    setSendingTo(room.roomId);
    // Предыдущий отказ гасится перед новой попыткой, иначе на экране висел
    // бы текст ошибки от прошлой комнаты во время успешной отправки.
    setError(null);
    try {
      await forwardMessage(client, item, event, room.roomId);
      setSentTo((prev) => new Set(prev).add(room.roomId));
    } catch (e) {
      setError(tfmt("forward.failed", { error: humanError(e) }));
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <Dialog
      title={t("forward.title")}
      onClose={onClose}
      error={error}
      footer={
        <DialogButton onClick={onClose} testId="forward-close">
          {t("common.close")}
        </DialogButton>
      }
    >
      <input
        type="search"
        value={query}
        placeholder={t("common.search")}
        aria-label={t("common.search")}
        data-testid="forward-search"
        onChange={(e) => setQuery(e.target.value)}
        style={{
          font: "inherit",
          fontSize: font.body,
          padding: `${space.sm}px ${space.md}px`,
          borderRadius: radius.sm,
          border: `1px solid ${color.border}`,
          background: color.bg,
          color: color.text,
          outline: "none",
        }}
      />
      <div
        role="listbox"
        aria-label={t("forward.title")}
        data-testid="forward-list"
        style={{
          // Высота ограничена, список прокручивается: комнат у человека
          // десятки, диалог не должен уезжать за экран.
          maxHeight: "50vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {candidates.length === 0 && (
          <div
            data-testid="forward-empty"
            style={{ padding: space.md, fontSize: font.small, color: palette.textDim }}
          >
            {t("emoji.nothingFound")}
          </div>
        )}
        {candidates.map((room) => (
          <ForwardRow
            key={room.roomId}
            client={client}
            room={room}
            sending={sendingTo === room.roomId}
            sent={sentTo.has(room.roomId)}
            // Повторное нажатие по ЛЮБОЙ комнате запрещено, пока идёт
            // отправка: двойной тап дал бы два одинаковых сообщения.
            disabled={sendingTo !== null}
            onForward={() => void forward(room)}
          />
        ))}
      </div>
    </Dialog>
  );
}

function ForwardRow({
  client,
  room,
  sending,
  sent,
  disabled,
  onForward,
}: {
  client: MatrixClient;
  room: RoomSummary;
  sending: boolean;
  sent: boolean;
  disabled: boolean;
  onForward: () => void;
}) {
  // Тот же аватар, что в списке чатов: качается с токеном, пока грузится —
  // инициалы на детерминированном цвете. Второй рисовалки быть не должно.
  const url = useAuthedAvatar(client, room.avatarUrl, size.avatar);
  const diameter = 36;

  return (
    <button
      type="button"
      role="option"
      aria-selected={sent}
      data-testid={`forward-room-${room.roomId}`}
      disabled={disabled}
      onClick={onForward}
      style={{
        font: "inherit",
        fontSize: font.body,
        color: color.text,
        display: "flex",
        alignItems: "center",
        gap: space.md,
        // Вся ширина — цель нажатия (пальцем на Android промахнуться по
        // узкой цели легко). Тот же приём, что у строк списка чатов.
        width: "100%",
        textAlign: "left",
        padding: `${space.sm}px ${space.md}px`,
        border: 0,
        background: "transparent",
        borderRadius: radius.sm,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled && !sending ? 0.6 : 1,
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          width={diameter}
          height={diameter}
          style={{ ...s.avatar, width: diameter, height: diameter }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            ...s.avatar,
            width: diameter,
            height: diameter,
            background: colorOf(room.roomId),
            color: color.textOnAccent,
          }}
        >
          {room.initials}
        </span>
      )}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {room.name}
      </span>
      {/* Три состояния строки, и ни одно не молчит: идёт отправка — спиннер,
          уже переслали — галочка, иначе пусто. */}
      {sending && (
        <>
          {/* Keyframes локальные, как в RoomTabs/RoomPage: глобального
              листа анимаций в проекте нет, каждый компонент объявляет свои. */}
          <style>{`@keyframes algForwardSpin { to { transform: rotate(360deg); } }`}</style>
          <span
            role="status"
            data-testid="forward-sending"
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: `2px solid ${color.accent}`,
              borderTopColor: "transparent",
              animation: "algForwardSpin 0.8s linear infinite",
            }}
          />
        </>
      )}
      {!sending && sent && (
        <span
          data-testid="forward-sent"
          style={{ color: color.accent, fontWeight: 700 }}
        >
          ✓
        </span>
      )}
    </button>
  );
}


